import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateFacilityIds, getConfigurationFacility } from "../../app/store/config/configActions";
import { State } from "../../app/store";
import { getStepSaleConfig } from "../../app/store/stepSaleConfig/saleStepConfigActions";

export function useFacilityIdManager() {
  const dispatch = useDispatch();
  const configuration = useSelector((state: State) => state.config.configuration);
  const userData = useSelector((state: State) => state.auth.userData);
  const isB2B = Boolean(userData.b2bType);

  const getFacilityIdFromUrl = () => {
    const queryParams = new URLSearchParams(window.location.search);
    // Najpierw próbujemy standardowego parametru "id"
    let facilityId = queryParams.get("id");

    // Jeśli parametr zawiera "/", pobieramy tylko część przed "/"
    if (facilityId && facilityId.includes("/")) {
      facilityId = facilityId.split("/")[0];
    }

    // Jeśli nie ma "id", sprawdzamy alternatywy
    if (!facilityId) {
      facilityId = queryParams.get("facilityIds") || queryParams.get("facilityId") || queryParams.get("ids");

      // Jeśli parametr jest w formacie "0,1212" - usuwamy "0,"
      if (facilityId && facilityId.startsWith("0,")) {
        facilityId = facilityId.substring(2);
      }

      if (facilityId) {
        // Usuwamy alternatywne parametry
        queryParams.delete("facilityIds");
        queryParams.delete("facilityId");
        queryParams.delete("ids");
        // Ustawiamy parametr "id" z odczytaną (i ewentualnie zmodyfikowaną) wartością
        queryParams.set("id", facilityId);
        const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
        window.history.replaceState({}, "", newUrl);
      }
    }
    return facilityId;
  };

  useEffect(() => {
    const handleFacilityId = async () => {
      try {
        const urlFacilityId = getFacilityIdFromUrl();
        const storedFacilityId = sessionStorage.getItem("facilityId");

        // Sprawdzamy czy mamy aktywną sesję B2B
        const b2bSession = new URLSearchParams(window.location.search).get("B2BSESSION");

        if (urlFacilityId && urlFacilityId !== "0") {
          // Jeśli mamy prawidłowy ID z URL, używamy go
          dispatch(updateFacilityIds(urlFacilityId));
          sessionStorage.setItem("facilityId", urlFacilityId);

          if (!configuration || Object.keys(configuration).length === 0) {
            dispatch(getConfigurationFacility(urlFacilityId));
            dispatch(getStepSaleConfig(urlFacilityId));
          }
        } else if (storedFacilityId && storedFacilityId !== "0") {
          // Jeśli brak ID w URL ale mamy w session storage, przywracamy do URL
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set("id", storedFacilityId);

          // Dla sesji B2B również przywracamy parametr B2BSESSION jeśli istnieje
          if (b2bSession) {
            newUrl.searchParams.set("B2BSESSION", b2bSession);
          }

          window.history.replaceState({}, "", newUrl.toString());
          dispatch(updateFacilityIds(storedFacilityId));
        } else if (b2bSession && !urlFacilityId) {
          // Dla sesji B2B bez ID - próbujemy odzyskać z sessionStorage
          const fallbackId = sessionStorage.getItem("facilityId");
          if (fallbackId && fallbackId !== "0") {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set("id", fallbackId);
            newUrl.searchParams.set("B2BSESSION", b2bSession);
            window.history.replaceState({}, "", newUrl.toString());
            dispatch(updateFacilityIds(fallbackId));
          }
        }
      } catch (error) {
        console.error("Error handling facility ID:", error);
      }
    };

    const checkForIdChange = () => {
      const newFacilityId = getFacilityIdFromUrl() || "";
      const storedFacilityId = sessionStorage.getItem("facilityId");

      if (!storedFacilityId && newFacilityId && newFacilityId !== "0") {
        sessionStorage.setItem("facilityId", newFacilityId);
      } else if (newFacilityId && newFacilityId !== storedFacilityId && newFacilityId !== "0") {
        sessionStorage.setItem("facilityId", newFacilityId);
        window.location.reload();
      }
    };

    handleFacilityId();

    window.addEventListener("popstate", checkForIdChange);
    window.addEventListener("pushstate", checkForIdChange);
    window.addEventListener("replaceState", checkForIdChange);

    return () => {
      window.removeEventListener("popstate", checkForIdChange);
      window.removeEventListener("pushstate", checkForIdChange);
      window.removeEventListener("replaceState", checkForIdChange);
    };
  }, [dispatch, configuration]);

  useEffect(() => {
    const facilityId = sessionStorage.getItem("facilityId");
    if (facilityId && facilityId !== "0" && userData.objectId && isB2B) {
      dispatch(getStepSaleConfig(facilityId));
    }
  }, [dispatch, isB2B, userData.objectId]);
}
